// Live API layer for the Crude Tender Opening module (List + read-only Detail).
//
// Implements formview-reference/tender-opening/api-contract-tender-opening.md — the
// TenderOpeningController (route base `v1/TenderOpening`) on the CRUDE host
// (`06.WebApi.Crude` -> crudeApi), the stage after ProcurementValidity is Submitted.
// Same host/auth/error conventions as procurementApi.js (which see for the crudeApi
// unwrap + string-reject contract).
//
// List (section 1) + Detail (section 2, read-only) are wired here. Edit / the
// per-step Save endpoints (Request HPS/Invite DMUT/Invite Panitia/Request Freight)
// are a later phase — the contract has none yet.
//
// Contract-specific notes baked in below:
//  - `id` in the List is TenderOpening.Id — used as {tenderOpeningId} for detail/edit
//    routes later, NOT Procurement.Id.
//  - DMUT is a SINGLE step field `inviteDmutStepStatus` (AD-034); the list's single
//    "Invite DMUT Document" column reads it.
//  - `phaseStatusName` is always "Open" for now — no endpoint flips the four steps yet
//    (contract caveat). Don't treat that as a bug.
import { crudeApi, crudeAxios } from '../../../../helpers/crude_api_helper';
import { REQUESTED } from './tenderOpeningConstants';
import { downloadDocument as downloadPlanningDoc } from '../Planning/planningApi';
import { downloadDocument as downloadProcurementDoc } from '../Procurement/procurementApi';

const BASE = '/v1/TenderOpening';

// Raw wire value for a Completed checklist step (`PlanningStepStatus`, reused). The
// list renders a green/grey checkmark per step, so a boolean is all we need.
const STEP_COMPLETED = 'Completed';

// List item (GetTenderOpeningListResponse) -> the flat shape TenderOpeningListView's
// columns read. The four `checks` keys must match CHECK_COLUMNS in
// tenderOpeningConstants.js.
export const normalizeListItem = r => ({
  id: r.id, // TenderOpening.Id — the {tenderOpeningId} route key
  procurementId: r.procurementId,
  procId: r.procurementCode,
  title: r.title,
  procurementType: r.jenisPengadaanName,
  procurementMethod: r.mekanismePengadaanName,
  description: r.description,
  checks: {
    requestHps: r.requestHpsStepStatus === STEP_COMPLETED,
    inviteDmutDocument: r.inviteDmutStepStatus === STEP_COMPLETED,
    invitePanitiaTender: r.invitePanitiaTenderStepStatus === STEP_COMPLETED,
    reqFreightAvailability:
      r.requestFreightAvailabilityStepStatus === STEP_COMPLETED,
  },
  createdDate: r.created,
  updatedDate: r.modified,
  mainStatus: r.phaseName, // always 'Tender Opening'
  toStatus: r.phaseStatusName, // 'Open' for now (see contract caveat)
});

// GET /v1/TenderOpening (paginated). `sortField` is PascalCase + whitelisted
// (contract section 1); `sortOrder` only applies when a field is set. crudeApi
// strips null/undefined/empty params, so pass `|| undefined` for the optionals.
export const listTenderOpening = ({
  page = 1,
  pageSize = 10,
  searchText = '',
  sortField = null,
  sortOrder = null,
} = {}) =>
  crudeApi
    .get(BASE, {
      page,
      pageSize,
      searchText: searchText || undefined,
      sortField: sortField || undefined,
      sortOrder: sortField ? sortOrder || undefined : undefined,
    })
    .then(res => ({
      items: (res?.items || []).map(normalizeListItem),
      totalCount: res?.totalCount ?? 0,
    }));

const SUMMARY = `${BASE}/GetTenderOpeningSummary`;

// GET /v1/TenderOpening/GetTenderOpeningSummary (contract section 1a) — server-side
// counts computed from TenderOpening.PhaseStatusId (a permanent snapshot, so still
// accurate after the header advances to the HPS phase). No query params, not filtered
// by search — the cards reflect the whole set. Returns the flat card shape; callers
// default missing values to 0.
//
// Note (contract 1a): open + inProgress + done need NOT equal total — rows in
// 'On Soft/Hard Recall' are counted in total but have no card bucket of their own.
export const summaryTenderOpening = () =>
  crudeApi.get(SUMMARY).then(res => ({
    total: res?.totalTenderOpening ?? 0,
    open: res?.open ?? 0,
    inProgress: res?.inProgress ?? 0,
    done: res?.done ?? 0,
  }));

// ===========================================================================
// Section 2 — GET /v1/TenderOpening/{tenderOpeningId} (Detail, read-only)
// ===========================================================================
//
// One combined endpoint for the whole Detail page (info card + 6 tabs). Modeled
// on procurementApi.normalizeDetail — the response echoes Planning/
// ProcurementValidity data plus the 4 per-step statuses. The three actionable
// step objects (`hpsRequest`/`invitePanitiaLelang`/`freightAvailability`) are
// ALWAYS null until their Save features are built (contract section 2) — that
// null is what drives the Detail page to hide the HPS/Freight info sections.

// crudes[] sub-row -> the shape CargoTable's nested table renders. Alpha
// Dyed/Undyed are not in the contract yet (BE question #2) -> default REQUESTED.
// `klasifikasi` (2026-07-24) is a live lookup from the current Crude master data
// (classificationYield) -> can be null if the source Crude no longer exists.
const normalizeCrude = cr => ({
  id: cr.id,
  crudeName: cr.crudeName,
  klasifikasi: cr.classificationYield || null,
  maxCapacity: cr.maxCapacity ?? null,
  remarkMaxCapacity: cr.maxCapacityNote || '',
  isViaCibOnly: !!cr.isViaCIBOnly,
  bleeding1: cr.blendingCrude1 ?? null,
  bleeding2: cr.blendingCrude2 ?? null,
  alphaDyed: REQUESTED,
  alphaUndyed: REQUESTED,
});

// cargos[] -> CargoTable rows. The four freight columns are not in the contract
// yet (BE question #3) -> default REQUESTED.
const normalizeCargo = c => ({
  id: c.id,
  cargoType: c.cargoCategoryName,
  cargoCategoryId: c.cargoCategoryId,
  ot: c.otPercentage ?? null,
  otOption: c.operationalToleranceOption || '', // "Buyer's Option" | "Seller's Option"
  volume: c.volumeKbbl ?? null,
  ddrFrom: c.ddrFrom || null,
  ddrTo: c.ddrTo || null,
  ddrNotes: c.ddrNotes || '',
  incoterms: (c.incoterms || []).map(i => i.incotermName),
  incotermsNotes: c.incotermsNotes || '',
  plant: c.plantName,
  unit: c.cduName,
  freightLumpsum: REQUESTED,
  laytime: REQUESTED,
  demurragePdpr: REQUESTED,
  r2: REQUESTED,
  crudes: (c.crudes || []).map(normalizeCrude),
});

const normalizeClause = c => ({
  id: c.id,
  clauseId: c.id,
  title: c.title,
  mandatory: !!c.isMandatoryForDmut,
  summernote: c.note || '',
});

// planningDocuments[] / procurementValidityDocuments[] entry -> the Supporting
// Document tab's row shape. `source` maps the API enum to the badge value the
// tab expects ('master' | 'custom'). `origin` tracks which array the document
// came from ('planning' | 'procurement') — NOT the same thing as `source`
// (MasterDocument/Custom) — and drives both the "Document Type" column (rule
// §Tab Supporting Document) and which download endpoint to call (contract
// §Download Dokumen: planningId vs procurementValidityId).
const DOC_SOURCE_MASTER = 'MasterDocument';
const normalizeDocument = (d, origin) => ({
  id: d.id,
  name: d.documentName,
  source: d.source === DOC_SOURCE_MASTER ? 'master' : 'custom',
  origin,
  showToDmut: !!d.showToDmut,
  fileName: d.fileName,
  fileContentType: d.fileContentType,
  fileSize: d.fileSize,
  storageFileId: d.storageFileId,
  lastUpdated: d.modified || d.created || null,
});

const stepDone = s => s === STEP_COMPLETED;

// "09:00:00" (TimeOnly) -> "09:00" for display.
const formatTimeHHmm = t => (t ? t.slice(0, 5) : t);

// Full Detail (GetTenderOpeningResponse) -> { record, detail } for the page.
export const normalizeDetail = api => {
  const record = {
    id: api.id, // TenderOpening.Id
    procurementId: api.procurementId,
    procId: api.procurementCode,
    title: api.title,
    procurementType: api.jenisPengadaanName,
    procurementMethod: api.mekanismePengadaanName,
    description: api.description,
    createdDate: api.created,
    mainStatus: api.phaseName, // always 'Tender Opening'
    toStatus: api.phaseStatusName, // 'Open' for now (contract caveat)
    // Needed to pick the right download endpoint for supportingDocuments[]
    // (contract §Download Dokumen — Tender Opening has no download of its own).
    planningId: api.planningId,
    procurementValidityId: api.procurementValidityId,
    checks: {
      requestHps: stepDone(api.requestHpsStepStatus),
      inviteDmutDocument: stepDone(api.inviteDmutStepStatus),
      invitePanitiaTender: stepDone(api.invitePanitiaTenderStepStatus),
      reqFreightAvailability: stepDone(
        api.requestFreightAvailabilityStepStatus
      ),
    },
  };

  const detail = {
    requirement: {
      contractType: api.contractType || '',
      startPeriod: api.contractStartPeriod || null,
      endPeriod: api.contractEndPeriod || null,
      additionalNotes: api.otherTermsNotes || '',
      cargos: (api.cargos || []).map(normalizeCargo),
      clauses: (api.clauses || []).map(normalizeClause),
      // Now echoed to Tender Opening Detail (BE question #1, resolved
      // 2026-07-24) — null only when ProcurementValidity was never submitted.
      validity: {
        submissionDeadline: api.submissionDeadline || null,
        validityPeriod: api.validityPeriod || null,
      },
    },
    // Last Soft Recall event (endpoint 6 — contract §230-235 / §315). All null
    // until the record has ever been Soft Recalled; `reason` is the optional
    // comment the user typed at that time. Surfaced in the Clause Detail Alert
    // while On Soft Recall.
    softRecall: {
      reason: api.softRecallReason || '',
      by: api.softRecallBy || '',
      at: api.softRecallAt || null,
    },
    // Merge the two read-only echo lists into the single Supporting Document
    // tab, tagging each with its origin array (drives the Document Type column
    // and the download endpoint — see normalizeDocument above).
    supportingDocuments: [
      ...(api.planningDocuments || []).map(d =>
        normalizeDocument(d, 'planning')
      ),
      ...(api.procurementValidityDocuments || []).map(d =>
        normalizeDocument(d, 'procurement')
      ),
    ],
    // BE-generated on Submit — null until the respective Send endpoint has run,
    // which hides the HPS/Freight info sections. Field names now match the
    // contract's actionable object shapes (section 2, "Shape objek tab
    // actionable saat sudah terisi").
    hps: api.hpsRequest
      ? {
          hpsNumber: api.hpsRequest.hpsMarketNumber || null,
          hpsRequestDate: api.hpsRequest.requestDate || null,
          notaDinasTitle: api.hpsRequest.notaDinasTitle || '',
          notaDinasNo: api.hpsRequest.notaDinasNumber || '',
          notaDinasDate: api.hpsRequest.notaDinasDate || null,
          notaDinasDocument: api.hpsRequest.notaDinasFileName || '',
        }
      : null,
    panitia: api.invitePanitiaLelang
      ? {
          tanggal: api.invitePanitiaLelang.meetingDate || null,
          waktu: api.invitePanitiaLelang.isUntilFinished
            ? null
            : [
                formatTimeHHmm(api.invitePanitiaLelang.startTime),
                formatTimeHHmm(api.invitePanitiaLelang.endTime),
              ]
                .filter(Boolean)
                .join(' - '),
          // Raw components (display-string `waktu` above can't be split back
          // safely) — needed to prefill the Edit-mode form.
          startTime: formatTimeHHmm(api.invitePanitiaLelang.startTime) || '',
          tanggalEnd: api.invitePanitiaLelang.meetingEndDate || null,
          endTime: formatTimeHHmm(api.invitePanitiaLelang.endTime) || '',
          hinggaSelesai: !!api.invitePanitiaLelang.isUntilFinished,
          lokasi: api.invitePanitiaLelang.location || '',
          agenda: api.invitePanitiaLelang.agenda || '',
          // Nota Dinas undangan evaluasi tender (desain acuan tab Panitia).
          notaDinasTitle: api.invitePanitiaLelang.notaDinasTitle || '',
          notaDinasNo: api.invitePanitiaLelang.notaDinasNumber || '',
          notaDinasDate: api.invitePanitiaLelang.notaDinasDate || null,
          notaDinasDocument: api.invitePanitiaLelang.notaDinasFileName || '',
        }
      : {},
    freight: api.freightAvailability
      ? {
          freightNumber:
            api.freightAvailability.freightCostRequestNumber || null,
          freightRequestDate:
            api.freightAvailability.freightCostRequestDate || null,
        }
      : null,
  };

  return { record, detail };
};

// GET /v1/Planning/{planningId}/Document/{documentId}/Download or
// GET /v1/ProcurementValidity/{procurementValidityId}/Document/{documentId}/Download
// (both blob) — Tender Opening has no download endpoint of its own (contract
// §Download Dokumen). `origin`/`ownerId` come from a normalized document row's
// `origin` and the matching `record.planningId`/`record.procurementValidityId`.
export const downloadSupportingDocument = (origin, ownerId, documentId) =>
  origin === 'planning'
    ? downloadPlanningDoc(ownerId, documentId)
    : downloadProcurementDoc(ownerId, documentId);

// GET /v1/TenderOpening/{tenderOpeningId}. Returns normalized { record, detail }.
// 404 when the id is unknown (contract section 2) — surfaced to the caller.
export const getTenderOpening = tenderOpeningId =>
  crudeApi.get(`${BASE}/${tenderOpeningId}`).then(normalizeDetail);

// ===========================================================================
// Edit-mode write endpoints (contract sections 3–10) — Send Request HPS,
// Invite DMUT + Soft Recall, Invite Panitia Lelang + Hard Recall, Request
// Freight Availability, Resubmit Clause. All re-fetch Detail after success
// (the caller re-runs getTenderOpening to sync status/checklist/badges).
// ===========================================================================

// `notaDinasDate` — a date-only value (from <input type="date">, already
// "YYYY-MM-DD"). BE stores it verbatim (contract §4, no server-side timezone
// conversion) and the view only ever renders the date (no time), so it is sent
// as-is with NO offset — same as meetingDate in the Panitia tab. Wrapping it in
// a +07:00 offset would double-shift once the value is echoed back and rendered
// in Jakarta.

// 3 — PUT /v1/TenderOpening/{id}/HpsRequest/Draft (multipart, upsert). 422 if
// requestHpsStepStatus is already Completed (contract: locked after Send).
export const saveHpsDraft = (
  tenderOpeningId,
  { notaDinasTitle, notaDinasNumber, notaDinasDate, file } = {}
) =>
  crudeApi.putMultipart(`${BASE}/${tenderOpeningId}/HpsRequest/Draft`, {
    notaDinasTitle: notaDinasTitle || undefined,
    notaDinasNumber: notaDinasNumber || undefined,
    notaDinasDate: notaDinasDate || undefined,
    file: file || undefined,
  });

// 4 — PUT /v1/TenderOpening/{id}/HpsRequest/Send (multipart). notaDinasTitle/
// Number/Date are required by the contract; file optional if a Draft already
// uploaded one (422 only when NEITHER this request NOR a prior Draft has a
// file — contract §4, added 2026-07-24). Response 200 -> { hpsMarketNumber,
// requestDate }.
export const sendHpsRequest = (
  tenderOpeningId,
  { notaDinasTitle, notaDinasNumber, notaDinasDate, file } = {}
) =>
  crudeApi
    .putMultipart(`${BASE}/${tenderOpeningId}/HpsRequest/Send`, {
      notaDinasTitle,
      notaDinasNumber,
      notaDinasDate,
      file: file || undefined,
    })
    .then(res => ({
      hpsNumber: res?.hpsMarketNumber ?? null,
      hpsRequestDate: res?.requestDate ?? null,
    }));

// 4a — GET /v1/TenderOpening/{id}/HpsRequest/NotaDinas/Download (blob). One
// endpoint per document type (contract §4a) — no documentId needed, HpsRequest
// is 1:1 with Procurement. 404 if tenderOpeningId is unknown OR no Nota Dinas
// file was ever uploaded.
export const downloadNotaDinas = tenderOpeningId =>
  crudeAxios.get(`${BASE}/${tenderOpeningId}/HpsRequest/NotaDinas/Download`, {
    responseType: 'blob',
  });

// DMUT recipients (Q6) — read the DMUT/PIC list saved during Planning. Tender
// Opening has no recipient list of its own; the same list lives on Planning.
// GET /v1/Planning/{planningId}/DmutInvitation (api-contract-plan.md section 21)
// reads ONLY the saved tblT_PlanningDmutList (+ Pics) — it does NOT call eRS and
// does NOT pull the other three Planning tabs, unlike the heavy
// GET /v1/Planning/{planningId} it replaces here. 404 if planningId is unknown.
// Returns the flat row array SendInvitationDmutTab renders.
const normalizeDmutRecipient = x => ({
  id: x.id,
  dmutCode: x.dmutCode,
  dmutName: x.dmutName,
  dmutCargoCategory: x.dmutCargoCategory,
  dmutCountry: x.dmutCountry,
  pics: (x.pics || []).map(p => ({
    id: p.id,
    picName: p.picName,
    picEmail: p.picEmail,
  })),
});

export const getDmutRecipients = planningId =>
  crudeApi
    .get(`/v1/Planning/${planningId}/DmutInvitation`)
    .then(res => (res?.items || []).map(normalizeDmutRecipient));

// 5 — PUT /v1/TenderOpening/{id}/InviteDmut/Send. No body. 422 if already
// Completed, if On Soft Recall (Resubmit Clause required first), or if no
// DMUT PIC has a valid email (contract).
export const sendInviteDmut = tenderOpeningId =>
  crudeApi.put(`${BASE}/${tenderOpeningId}/InviteDmut/Send`);

// 6 — PUT /v1/TenderOpening/{id}/InviteDmut/SoftRecall. `comment` optional —
// flips inviteDmutStepStatus back to InProgress and phaseStatusName to
// "On Soft Recall".
export const softRecallDmut = (tenderOpeningId, comment) =>
  crudeApi.put(`${BASE}/${tenderOpeningId}/InviteDmut/SoftRecall`, {
    comment: comment || undefined,
  });

// 7 — PUT /v1/TenderOpening/{id}/InvitePanitiaLelang/HardRecall. `comment`
// optional — resets Planning/ProcurementValidity/TenderOpening (cross-module
// effect per contract); caller redirects back to the List after success since
// this record's own status changes to "On Hard Recall".
export const hardRecallPanitia = (tenderOpeningId, comment) =>
  crudeApi.put(`${BASE}/${tenderOpeningId}/InvitePanitiaLelang/HardRecall`, {
    comment: comment || undefined,
  });

// 8 — PUT /v1/TenderOpening/{id}/FreightAvailability/Send. No body. 422 if
// already Completed. Response 200 -> { freightCostRequestNumber,
// freightCostRequestDate }.
export const sendFreight = tenderOpeningId =>
  crudeApi
    .put(`${BASE}/${tenderOpeningId}/FreightAvailability/Send`)
    .then(res => ({
      freightNumber: res?.freightCostRequestNumber ?? null,
      freightRequestDate: res?.freightCostRequestDate ?? null,
    }));

// 9 — PUT /v1/TenderOpening/{id}/InvitePanitiaLelang/Send. Kini MULTIPART
// (desain acuan): jadwal rapat + Nota Dinas undangan evaluasi tender + file.
// meetingDate/startTime/location/agenda + notaDinasTitle/Number/Date required;
// endTime required unless isUntilFinished; file wajib kecuali Draft sebelumnya
// sudah mengunggah. meetingDate/startTime/endTime stored verbatim, no
// server-side timezone conversion — sent as plain date/time strings.
const buildPanitiaForm = ({
  meetingDate,
  startTime,
  meetingEndDate,
  endTime,
  isUntilFinished,
  location,
  agenda,
  notaDinasTitle,
  notaDinasNumber,
  notaDinasDate,
  file,
} = {}) => ({
  meetingDate: meetingDate || undefined,
  startTime: startTime || undefined,
  meetingEndDate: isUntilFinished ? undefined : meetingEndDate || undefined,
  endTime: isUntilFinished ? undefined : endTime || undefined,
  isUntilFinished: !!isUntilFinished,
  location: location || undefined,
  agenda: agenda || undefined,
  notaDinasTitle: notaDinasTitle || undefined,
  notaDinasNumber: notaDinasNumber || undefined,
  notaDinasDate: notaDinasDate || undefined,
  file: file || undefined,
});

export const sendInvitePanitia = (tenderOpeningId, payload) =>
  crudeApi.putMultipart(
    `${BASE}/${tenderOpeningId}/InvitePanitiaLelang/Send`,
    buildPanitiaForm(payload)
  );

// 9a — PUT /v1/TenderOpening/{id}/InvitePanitiaLelang/Draft (multipart,
// upsert). Partial data allowed; 422 setelah Send (step Completed).
export const saveInvitePanitiaDraft = (tenderOpeningId, payload) =>
  crudeApi.putMultipart(
    `${BASE}/${tenderOpeningId}/InvitePanitiaLelang/Draft`,
    buildPanitiaForm(payload)
  );

// 9b — GET /v1/TenderOpening/{id}/InvitePanitiaLelang/NotaDinas/Download
// (blob). 404 kalau belum ada file.
export const downloadPanitiaNotaDinas = tenderOpeningId =>
  crudeAxios.get(
    `${BASE}/${tenderOpeningId}/InvitePanitiaLelang/NotaDinas/Download`,
    { responseType: 'blob' }
  );

// 10 — PUT /v1/TenderOpening/{id}/Requirement/Clauses. Bulk update, only
// isMandatoryForDmut/note per clause (id must match an existing clause — no
// add/remove). Only callable while phaseStatusName === "On Soft Recall"
// (422 otherwise, contract). Exits On Soft Recall on success.
export const resubmitClauses = (tenderOpeningId, clauses) =>
  crudeApi.put(`${BASE}/${tenderOpeningId}/Requirement/Clauses`, {
    clauses: (clauses || []).map(c => ({
      id: c.clauseId || c.id,
      isMandatoryForDmut: !!c.mandatory,
      note: c.summernote || '',
    })),
  });
